import {
  CopyIcon,
  PaperPlaneIcon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { FormSummary, FormsService } from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import InquiryLinks from "components/Forms/InquiryLinks";
import Pager from "components/Dashboard/PeoplePanel/Pager";
import { usePaged } from "components/Forms/usePaged";
import { formatDate } from "components/Forms/ResponseDialog";
import { OrgAdminShell } from "pages/org-performance/OrgAdminPage";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notifications from "util/notifications";

function FormsList({ orgId }: { orgId: number }) {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormSummary[]>();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<FormSummary | null>(null);
  const [duplicating, setDuplicating] = useState<FormSummary | null>(null);
  const [copying, setCopying] = useState(false);
  const [sending, setSending] = useState<FormSummary | null>(null);
  const formsPage = usePaged(forms);

  const load = useCallback(() => {
    FormsService.getForms({ orgId })
      .then(setForms)
      .catch((e) =>
        Notifications.error(errorText(e, "Forms could not be loaded."))
      );
  }, [orgId]);

  useEffect(load, [load]);

  function createForm() {
    setCreating(true);
    FormsService.createForm({ orgId })
      .then((form) => navigate(`/forms/${form.id}/edit`))
      .catch((e) => {
        Notifications.error(errorText(e, "The form could not be created."));
        setCreating(false);
      });
  }

  /** A copy to start a new form from, opened in the builder. */
  function duplicateForm(form: FormSummary) {
    setCopying(true);
    FormsService.duplicateForm({ formId: form.id })
      .then((copy) => navigate(`/forms/${copy.id}/edit`))
      .catch((e) => {
        Notifications.error(errorText(e, "The form could not be copied."));
        setCopying(false);
      });
  }

  function deleteForm(form: FormSummary) {
    FormsService.deleteForm({ formId: form.id })
      .then(() => {
        setDeleting(null);
        setForms((prev) => (prev || []).filter((f) => f.id !== form.id));
        Notifications.success(`${form.name} was deleted.`);
      })
      .catch((e) =>
        Notifications.error(errorText(e, "The form could not be deleted."))
      );
  }

  const newFormButton = (
    <Button
      color={ButtonColor.GREEN}
      size={ButtonSize.SMALL}
      onClick={createForm}
      disabled={creating}
    >
      <PlusIcon /> New form
    </Button>
  );

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Forms</h2>
        {forms && forms.length > 0 && newFormButton}
      </div>

      {!forms && (
        <div className="mt-6 animate-pulse space-y-3">
          <div className="h-10 rounded bg-gray-200" />
          <div className="h-10 rounded bg-gray-100" />
        </div>
      )}

      {forms && forms.length === 0 && (
        <div className="mt-10 mb-6 flex flex-col items-center text-center">
          <p className="text-lg font-semibold text-gray-900">No forms yet</p>
          <p className="mt-2 max-w-md text-sm text-gray-600">
            Build a form once, then send it to families and tutors, or put it on
            your website. Their answers go straight into Tadpole.
          </p>
          <div className="mt-5">{newFormButton}</div>
        </div>
      )}

      {forms && forms.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          {formsPage.paged && (
            <div className="mb-3">
              <Pager
                page={formsPage.page}
                pageCount={formsPage.pageCount}
                onPage={formsPage.setPage}
              />
            </div>
          )}
          <table className="w-full border-collapse bg-white rounded-lg">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3 text-sm font-bold">Name</th>
                <th className="text-left p-3 text-sm font-bold">Filled out</th>
                <th className="text-left p-3 text-sm font-bold">Waiting</th>
                <th className="text-left p-3 text-sm font-bold">Last edited</th>
                <th className="p-3 text-sm font-bold text-right" />
              </tr>
            </thead>
            <tbody>
              {formsPage.pageRows.map((form) => (
                <tr
                  key={form.id}
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/forms/${form.id}`)}
                >
                  <td className="p-3 text-sm font-medium text-gray-900">
                    {form.name}
                    {form.audience === FormSummary.audience.INQUIRY && (
                      <span className="block text-xs font-normal text-gray-500">
                        Website inquiry
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {form.filled_count}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {form.waiting_count}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {formatDate(form.modified_at)}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <span className="inline-flex gap-2">
                      {form.public_id && (
                        <InquiryLinks
                          publicId={form.public_id}
                          formName={form.name}
                        />
                      )}
                      <IconTip label="Edit">
                        <Button
                          color={ButtonColor.PURPLE}
                          size={ButtonSize.SMALL}
                          aria-label={`Edit ${form.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/forms/${form.id}/edit`);
                          }}
                        >
                          <Pencil1Icon />
                        </Button>
                      </IconTip>
                      {/* A website inquiry is filled out from the website,
                          never sent: its room stays, so the icons line up. */}
                      {form.audience === FormSummary.audience.INQUIRY ? (
                        <span className="invisible" aria-hidden="true">
                          <Button size={ButtonSize.SMALL} tabIndex={-1}>
                            <PaperPlaneIcon />
                          </Button>
                        </span>
                      ) : (
                        <IconTip label="Send">
                          <Button
                            color={ButtonColor.GREEN}
                            fill={ButtonFill.HOLLOW}
                            size={ButtonSize.SMALL}
                            aria-label={`Send ${form.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSending(form);
                            }}
                          >
                            <PaperPlaneIcon />
                          </Button>
                        </IconTip>
                      )}
                      <IconTip label="Duplicate">
                        <Button
                          color={ButtonColor.PURPLE}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.SMALL}
                          aria-label={`Duplicate ${form.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDuplicating(form);
                          }}
                        >
                          <CopyIcon />
                        </Button>
                      </IconTip>
                      <IconTip label="Delete">
                        <Button
                          color={ButtonColor.RED}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.SMALL}
                          aria-label={`Delete ${form.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(form);
                          }}
                        >
                          <TrashIcon />
                        </Button>
                      </IconTip>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={sending !== null}
        onOpenChange={(open) => !open && setSending(null)}
      >
        {sending && (
          <ComposeMessageDialog
            org_id={orgId}
            initialFormId={sending.id}
            lookup={sending.audience}
          />
        )}
      </Dialog>

      <Dialog
        open={duplicating !== null}
        onOpenChange={(open) => !open && setDuplicating(null)}
      >
        {duplicating && (
          <DialogContent className="dialog-content">
            <h2 className="text-xl font-semibold mb-2">
              Duplicate {duplicating.name}?
            </h2>
            <p className="text-sm text-gray-700">
              This makes a new form called &quot;Copy of {duplicating.name}
              &quot; with the same sections, fields and settings. The original
              form doesn&apos;t change, and its answers stay with it.
              {duplicating.audience === FormSummary.audience.INQUIRY &&
                " The copy gets its own website link, so the link on your website still opens the original."}{" "}
              The copy opens in the builder so you can change it.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.PURPLE}
                disabled={copying}
                onClick={() => duplicateForm(duplicating)}
              >
                Duplicate
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        {deleting && (
          <DialogContent className="dialog-content">
            <h2 className="text-xl font-semibold mb-2">
              Delete {deleting.name}?
            </h2>
            <p className="text-sm text-gray-700">
              It can no longer be sent or filled out. Forms people already
              filled out stay on their pages.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={() => deleteForm(deleting)}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

export default function FormsPage() {
  return (
    <OrgAdminShell>{(orgId) => <FormsList orgId={orgId} />}</OrgAdminShell>
  );
}
